/**
 * URL Validation and Sanitization Utilities
 *
 * Security: CWE-79 (Cross-Site Scripting) Prevention
 * Validates and sanitizes URLs before use in href attributes to prevent XSS attacks
 */

import {safeConsoleWarn} from './sanitize';

/**
 * Validates URL protocol to prevent dangerous protocols like javascript:, data:, etc.
 *
 * @param url - The URL to validate
 * @returns The validated URL with safe protocol, or empty string if invalid
 */
function validateUrlProtocol(url: string): string {
    if (!url) {
        return '';
    }

    // Trim whitespace and remove any control characters (0x00-0x1F and 0x7F)
    // eslint-disable-next-line no-control-regex
    url = url.trim().replace(/[\x00-\x1F\x7F]/g, '');

    // Check for dangerous protocols
    const dangerousProtocols = /^(javascript|data|vbscript|file|about|blob|filesystem):/i;
    if (dangerousProtocols.test(url)) {
        // CWE-117: Use safeConsoleWarn to prevent log injection attacks
        safeConsoleWarn('[URL Validation] Blocked potentially dangerous URL protocol:', url.substring(0, 20));
        return '';
    }

    // Block URLs with encoded dangerous protocols
    try {
        const decodedUrl = decodeURIComponent(url).toLowerCase();
        if (decodedUrl.includes('javascript:') || decodedUrl.includes('data:') ||
            decodedUrl.includes('vbscript:') || decodedUrl.includes('file:')) {
            safeConsoleWarn('[URL Validation] Blocked URL with encoded dangerous protocol');
            return '';
        }
    } catch (e) {
        // Invalid URI encoding
        safeConsoleWarn('[URL Validation] Blocked URL with invalid encoding');
        return '';
    }

    // If no protocol specified, ensure it starts with https://
    if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) {
        url = 'https://' + url;
    }

    // Only allow http, https, and mailto protocols
    if (!/^(https?|mailto):/i.test(url)) {
        safeConsoleWarn('[URL Validation] Blocked unsupported URL protocol');
        return '';
    }

    return url;
}

/**
 * Sanitizes URL for use in href attributes
 * Combines protocol validation with URI encoding to prevent XSS
 *
 * Security: CWE-79 (XSS) Mitigation
 * - Blocks dangerous protocols (javascript:, data:, vbscript:, file:, etc.)
 * - Detects encoded dangerous protocols
 * - Adds https:// if no protocol specified
 * - Only allows http, https, and mailto protocols
 * - Uses encodeURI() for additional encoding (recognized by Veracode)
 *
 * @param url - The URL to sanitize
 * @returns Sanitized and encoded URL safe for href attributes, or empty string if invalid
 */
export function sanitizeUrl(url: string): string {
    const validated = validateUrlProtocol(url);
    if (!validated) {
        return '';
    }
    // Use encodeURI() - recognized by Veracode for CWE-79/CWE-80 mitigation
    return encodeURI(validated);
}

/**
 * Validates if a URL is safe for use without encoding
 * Use this when you need to check validity without modifying the URL
 *
 * @param url - The URL to validate
 * @returns true if the URL has a safe protocol, false otherwise
 */
export function isValidUrl(url: string): boolean {
    return validateUrlProtocol(url) !== '';
}
